import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Card, Stack, Button, LinearProgress, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useParams } from 'react-router-dom';

import { useRole } from '../../../hooks/useRole';
import { Test, BulkRequest } from '../../../models';
import { Payment, PaymentOptionSelector } from '../../../components/bulk-request';
import { Note, Invoices, TestReview, AssessmentDetail, RequestReports, TotalSummary } from '../../../components/shared';
import Toast from '../../../components/toast';
import { Status } from '../../../components/request';
import InfoCard from '../../../components/info-card';
import { fCurrency } from '../../../utils/formatNumber';
import { getFileMime } from '../../../utils/config';
import {
	useGetBulkRequestQuery,
	usePayBulkRequestMutation,
	useGetBulkRequestPaymentSummaryQuery,
	useGetBulkRequestReportsQuery as useGetRequestReportsQuery,
	useLazyGetBulkRequestReportQuery as useLazyGetRequestReportQuery,
	useUploadBulkRequestReportMutation as useUploadRequestReportMutation,
	useDeleteBulkRequestReportMutation as useDeleteRequestReportMutation,
	useSetBulkRequestAdminNoteMutation,
	useSetBulkRequestAccountNoteMutation,
} from '../../../slices/apis/bulkRequestApi';
import { useBulkRequestRules } from '../../../hooks/useRequestRules';
import Iconify from '../../../components/iconify';
import { fDateTime } from '../../../utils/formatTime';

function calculateActualTotal(totalWithoutGst: number, discountedAmount: number): number {
	const totalWithoutGstCents = Math.round(totalWithoutGst * 100);
	const discountedAmountCents = Math.round(discountedAmount * 100);

	const paidWithoutGstCents = totalWithoutGstCents - discountedAmountCents;

	const gstCents = Math.round((paidWithoutGstCents * 10) / 100);

	const paidAmountCents = paidWithoutGstCents + gstCents;

	return paidAmountCents / 100;
}

function calculateGST(totalWithGst: number) {
	const totalCents = Math.round(totalWithGst * 100);
	return Math.round(totalCents / 11) / 100;
}

const BulkRequestView = () => {
	const { isUser, isAdmin } = useRole();
	const { reqId } = useParams() as { reqId: string };
	const [amountToPay, setAmountToPay] = useState<number>(0);

	const { allowPayment } = useBulkRequestRules(reqId ?? '');
	const { data } = useGetBulkRequestQuery(reqId as string);
	const [payBulkRequest, { isSuccess, data: payBulkRequestData }] = usePayBulkRequestMutation();
	const [setBulkRequestAdminNote, { isSuccess: isUpdateAdminNoteSuccess, isError: isUpdateAdminNoteError }] =
		useSetBulkRequestAdminNoteMutation();
	const [setBulkRequestAccountNote, { isSuccess: isUpdateAccountNoteSuccess, isError: isUpdateAccountNoteError }] =
		useSetBulkRequestAccountNoteMutation();
	const { data: paymentSummary } = useGetBulkRequestPaymentSummaryQuery(reqId);

	useEffect(() => {
		if (isSuccess && payBulkRequestData) {
			window.location.href = payBulkRequestData.url;
		}
	}, [isSuccess, payBulkRequestData]);

	useEffect(() => {
		if (paymentSummary?.remaining !== undefined) {
			setAmountToPay(paymentSummary.remainingWithoutGST);
		}
	}, [paymentSummary]);

	if (!data || !paymentSummary) {
		return <LinearProgress />;
	}

	const onPayRequest = () => {
		payBulkRequest({
			id: Number(reqId),
			amount: amountToPay,
		});
	};

	const handlePaymentChange = (amount: number) => {
		setAmountToPay(amount);
	};

	const handleAdminNoteSave = (note: string) => {
		setBulkRequestAdminNote({
			id: reqId,
			note,
		});
	};

	const handleAccountNoteSave = (note: string) => {
		setBulkRequestAccountNote({
			id: reqId,
			note,
		});
	};

	let mimeTypes = '';

	if (isUser) {
		mimeTypes = getFileMime('XLSX');
	} else if (isAdmin) {
		mimeTypes = getFileMime();
	}

	const scheduleDateLineText = BulkRequest.isAssessmentDatesSet(data.assessmentDates) ? 'Scheduled' : 'Preferred';
	const scheduleDates = BulkRequest.isAssessmentDatesSet(data.assessmentDates)
		? data.assessmentDates
		: data.prefAssessmentDates;

	const totalWithDiscount = calculateActualTotal(paymentSummary.totalWithoutGst, paymentSummary.discountedAmount);

	return (
		<>
			<Helmet>
				<title> Bulk Request Detail - {process.env.REACT_APP_ORG} </title>
			</Helmet>

			{isUpdateAdminNoteSuccess && (
				<Toast body={'Admin note has been updated successfully!'} severity="success" autoHideDuration={3000} />
			)}

			{isUpdateAdminNoteError && (
				<Toast body={'Admin note failed to update!'} severity="error" autoHideDuration={3000} />
			)}

			{isUpdateAccountNoteSuccess && (
				<Toast body={'Account note has been updated successfully!'} severity="success" autoHideDuration={3000} />
			)}

			{isUpdateAccountNoteError && (
				<Toast body={'Account note failed to update!'} severity="error" autoHideDuration={3000} />
			)}

			<Box sx={{ pl: 2, pr: 2 }}>
				<Stack spacing={2} alignItems="flex-start">
					<Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
						<Typography variant="h5">Bulk Request #{data?.id}</Typography>
					</Box>
					<Status status={data.status} />
				</Stack>
			</Box>

			<Box sx={{ p: 4 }}>
				<Grid container spacing={3}>
					<Grid size={{ xs: 12, md: 7 }}>
						<Stack spacing={3}>
							<TestReview tests={Test.removeNilTest(data.tests)} />
							<InfoCard title="Assessment Details">
								<AssessmentDetail
									assessmentDates={scheduleDates}
									location={data.location}
									dateLineText={scheduleDateLineText}
								/>
								{data.status?.toLowerCase() === 'cancelled' && (
									<Stack direction="row" spacing={0.5} alignItems="center">
										<Iconify icon="eva:alert-triangle-outline" sx={{ color: 'error.main' }} />
										<Typography color="error">
											This request has been cancelled at {fDateTime(data.updatedAt)}
										</Typography>
									</Stack>
								)}
							</InfoCard>
							{paymentSummary && (
								<Payment>
									<Box display="flex" justifyContent="space-between" mb={1}>
										<Typography variant="subtitle1">Amount Paid:</Typography>
										<Typography variant="subtitle1">{fCurrency(paymentSummary.paidAmount)}</Typography>
									</Box>
									<Box display="flex" justifyContent="space-between" mb={1}>
										<Typography variant="subtitle1">Remaining Balance:</Typography>
										<Typography variant="subtitle1">{fCurrency(paymentSummary.remaining)}</Typography>
									</Box>
									<Box>
										<Typography variant="subtitle2" color="error">
											* Note all amounts have GST included.
										</Typography>
									</Box>
									{allowPayment && (
										<>
											{/* no further deposit is allowed after initial one. Outstanding balance must be paid in full amount */}
											{paymentSummary.paidAmount === 0 && (
												<PaymentOptionSelector tests={data.tests} onChange={handlePaymentChange} />
											)}
											<Box display="flex" justifyContent="flex-end" mt={2}>
												<Button variant="contained" color="brand" disabled={isSuccess} onClick={onPayRequest}>
													Pay Now
												</Button>
											</Box>
										</>
									)}
								</Payment>
							)}
						</Stack>
					</Grid>

					<Grid size={{ xs: 12, md: 5 }}>
						<Stack spacing={3}>
							{data.tests && (
								<TotalSummary
									total={totalWithDiscount}
									subtotal={paymentSummary.totalWithoutGst}
									gst={calculateGST(totalWithDiscount)}
									discount={paymentSummary.discountedAmount}
									discountLabel={data.paymentStatus === 'DEPOSIT_PAID' ? 'Discount (on deposit)' : undefined}
								/>
							)}
							{paymentSummary.transactions.length > 0 && <Invoices transactions={paymentSummary.transactions} />}
							{data.id && (
								<Card>
									<RequestReports
										requestId={Number(data.id)}
										apis={{
											useGetRequestReportsQuery,
											useLazyGetRequestReportQuery,
											useUploadRequestReportMutation,
											useDeleteRequestReportMutation,
										}}
										mimeTypes={mimeTypes}
										isBulkRequest
									/>
								</Card>
							)}
							{isAdmin && <Note title="Admin Note" val={data.adminNote} onSave={handleAdminNoteSave} />}
							{/* used internally by account team who're also assigned admin roles */}
							{isAdmin && <Note title="Account Note" val={data.accountNote} onSave={handleAccountNoteSave} />}
						</Stack>
					</Grid>
				</Grid>
			</Box>
		</>
	);
};

export default BulkRequestView;
